<template>
  <!-- Contenedor fijo: se mide la altura para que el contenido use --header-height y no quede hueco -->
  <div class="fixed inset-x-0 top-0 z-40 flex flex-col pointer-events-none">
    <div class="flex flex-col shrink-0 pointer-events-auto">
      <!-- Línea delgada amarilla: publicidad (colapsa al hacer scroll) -->
      <Transition name="banner-collapse">
        <div
          v-show="showBanner"
          class="banner-block text-gray-900 text-center py-1.5 sm:py-1.5 px-3 sm:px-4 text-[10px] sm:text-xs font-medium shrink-0 leading-tight"
          style="background-color: #fdacc2;"
        >
          <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            <span class="hidden sm:inline">FREE SHIPPING ON ORDERS OVER $45</span>
            <span class="sm:hidden">FREE SHIPPING ON ORDERS OVER $45</span>
          </div>
        </div>
      </Transition>

      <!-- Header: logo, búsqueda, perfil, carrito (transparencia al hacer scroll). Solo esta altura define --header-height para que el contenido no salte al mostrar/ocultar la publicidad. -->
      <header
        ref="headerBarRef"
        :class="[
          'shrink-0 transition-all duration-300',
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
        ]"
      >
        <nav class="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center h-14 md:h-20">
            <!-- Logo: más pequeño en móvil -->
            <div class="flex items-center min-w-0 flex-1 md:flex-initial">
              <RouterLink to="/" class="flex items-center cursor-pointer group" @click="closeMobileMenu">
                <img 
                  src="/logos/logo1.png" 
                  alt="SupplemEarth" 
                  class="h-7 sm:h-8 md:h-11 w-auto max-h-10 md:max-h-none object-contain object-left transform group-hover:scale-105 transition-transform duration-300"
                />
              </RouterLink>
            </div>

            <!-- Right: en móvil solo carrito + hamburguesa; en desktop búsqueda + perfil + carrito -->
            <div class="flex items-center gap-0.5 sm:gap-1 md:space-x-2 flex-shrink-0">
              <!-- Search Bar - Solo desktop -->
              <div class="hidden md:flex relative search-container">
                <div class="relative">
                  <input 
                    type="text"
                    v-model="searchQuery"
                    @keyup.enter="handleSearch"
                    @focus="showSearchResults = searchQuery.trim().length > 0 && searchResults.length > 0"
                    placeholder="Search products..."
                    :class="[
                      'w-56 px-4 py-2.5 pl-10 pr-10 rounded-full text-sm focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 placeholder-gray-400 transition-all',
                      isScrolled ? 'bg-white/50 border border-white/30' : 'bg-gray-50 border border-gray-200'
                    ]"
                    style="--tw-ring-color: #EDD97A;"
                  />
                  <button
                    @click="handleSearch"
                    class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 transition-colors cursor-pointer z-10"
                    aria-label="Search"
                  >
                    <svg class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                  
                  <!-- Search Results Dropdown -->
                  <Transition
                    enter-active-class="transition-all duration-200 ease-out"
                    leave-active-class="transition-all duration-150 ease-in"
                    enter-from-class="opacity-0 scale-95 translate-y-2"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 translate-y-2"
                  >
                    <div
                      v-if="showSearchResults"
                      class="absolute top-full left-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[500px] overflow-y-auto z-50"
                    >
                      <div class="p-2">
                        <!-- Loading State -->
                        <div v-if="searchLoading" class="p-4 text-center">
                          <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                          <p class="mt-2 text-sm text-gray-600">Searching...</p>
                        </div>
                        
                        <!-- Results -->
                        <div v-else-if="searchResults.length > 0">
                          <div class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                            Products ({{ searchResults.length }})
                          </div>
                          <div
                            v-for="product in searchResults"
                            :key="product.id"
                            @click="goToProduct(product.id)"
                            class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                          >
                            <div class="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                              <img
                                :src="product.image"
                                :alt="product.name"
                                class="w-full h-full object-contain"
                              />
                            </div>
                            <div class="flex-1 min-w-0">
                              <h4 class="font-semibold text-gray-900 text-sm group-hover:text-green-600 transition-colors truncate">
                                {{ product.name }}
                              </h4>
                              <p class="text-xs text-gray-500 truncate">{{ product.category }}</p>
                              <p class="text-sm font-bold text-gray-900 mt-1">${{ product.price }}</p>
                            </div>
                          </div>
                          <div
                            v-if="searchQuery.trim()"
                            @click="handleSearch"
                            class="px-3 py-2 text-sm font-semibold text-center border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            style="color: #EDD97A;"
                          >
                            View all results for "{{ searchQuery }}"
                          </div>
                        </div>
                        
                        <!-- No Results -->
                        <div v-else-if="searchQuery.trim() && !searchLoading" class="p-4 text-center">
                          <svg class="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p class="text-sm text-gray-600">No products found</p>
                          <button
                            @click="handleSearch"
                            class="mt-2 text-sm font-semibold"
                            style="color: #EDD97A;"
                          >
                            Search all products
                          </button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>

              <!-- Profile / Login - Solo desktop (en móvil va dentro del menú hamburguesa) -->
              <div class="hidden md:block">
                <UserMenu v-if="userStore.isAuthenticated" />
                <button
                  v-else
                  @click="openLogin"
                  :class="[
                    'p-2.5 rounded-full transition-all cursor-pointer',
                    isScrolled ? 'text-gray-900 hover:bg-white/50' : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                  ]"
                  aria-label="User profile"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              </div>

              <!-- Shopping Cart: icono más compacto en móvil -->
              <button
                @click="openCart"
                :class="[
                  'relative p-2 md:p-2.5 rounded-full transition-all cursor-pointer',
                  isScrolled ? 'text-gray-900 hover:bg-white/50' : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                ]"
                aria-label="Shopping cart"
              >
                <svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <!-- Cart Item Count Badge -->
                <span 
                  v-if="cartItemCount > 0"
                  class="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 text-white text-[10px] md:text-xs font-bold rounded-full w-4 h-4 md:w-5 md:h-5 flex items-center justify-center"
                  style="background-color: #EDD97A;"
                >
                  {{ cartItemCount }}
                </span>
              </button>

              <!-- Hamburguesa: solo móvil/tablet -->
              <button
                @click="toggleMobileMenu"
                :class="[
                  'md:hidden p-2 rounded-full cursor-pointer transition-all',
                  isScrolled ? 'text-gray-900 hover:bg-white/50' : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                ]"
                aria-label="Toggle menu"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </nav>

        <!-- Login Modal -->
        <LoginModal :is-open="modalStore.isLoginModalOpen" @close="modalStore.closeLoginModal" />

        <!-- Cart Sidebar -->
        <CartSidebar />
      </header>

      <!-- Menú hamburguesa: overlay fuera del header para que no cambie la altura y no quede espacio en blanco al cerrar -->
      <Teleport to="body">
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-150 ease-in"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="isMobileMenuOpen"
            class="md:hidden fixed inset-0 z-30"
            @click.self="closeMobileMenu"
          >
            <!-- Backdrop -->
            <div class="absolute inset-0 bg-black/40" aria-hidden="true" />
            <!-- Panel: empieza con margen bajo el header para que el buscador no quede tapado -->
            <div
              class="absolute left-0 right-0 bottom-0 bg-gray-50 shadow-2xl overflow-y-auto rounded-t-2xl"
              :style="{ top: 'calc(var(--header-height, 80px) + 12px)' }"
              @click.stop
            >
              <div class="pt-12 pb-4 px-4 space-y-6 max-h-[calc(100vh-var(--header-height,80px)-12px)] overflow-y-auto">
                <!-- Búsqueda dentro del menú -->
                <div class="relative search-container">
                  <div class="relative">
                    <input 
                      type="text"
                      v-model="searchQuery"
                      @keyup.enter="handleSearch"
                      @focus="showSearchResults = searchQuery.trim().length > 0 && searchResults.length > 0"
                      placeholder="Search products..."
                      class="w-full px-4 py-3 pl-10 pr-11 rounded-xl text-sm focus:outline-none focus:ring-2 border border-gray-200 bg-white"
                      style="--tw-ring-color: #EDD97A;"
                    />
                    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <button
                      @click="handleSearch"
                      type="button"
                      class="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                      aria-label="Search"
                    >
                      <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                  <!-- Resultados búsqueda móvil -->
                  <Transition
                    enter-active-class="transition-all duration-150 ease-out"
                    leave-active-class="transition-all duration-100 ease-in"
                    enter-from-class="opacity-0 translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-1"
                  >
                    <div
                      v-if="showSearchResults"
                      class="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 max-h-[280px] overflow-y-auto z-50"
                    >
                      <div class="p-2">
                        <div v-if="searchLoading" class="p-3 text-center text-sm text-gray-500">Searching...</div>
                        <template v-else-if="searchResults.length > 0">
                          <div
                            v-for="product in searchResults"
                            :key="product.id"
                            @click="goToProduct(product.id)"
                            class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <div class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                              <img :src="product.image" :alt="product.name" class="w-full h-full object-contain" />
                            </div>
                            <div class="flex-1 min-w-0">
                              <p class="font-medium text-gray-900 text-sm truncate">{{ product.name }}</p>
                              <p class="text-xs text-gray-500">${{ product.price }}</p>
                            </div>
                          </div>
                          <button
                            v-if="searchQuery.trim()"
                            @click="handleSearch"
                            class="w-full px-3 py-2 text-sm font-semibold text-center rounded-lg hover:bg-gray-50"
                            style="color: #EDD97A;"
                          >
                            View all for "{{ searchQuery }}"
                          </button>
                        </template>
                        <div v-else-if="searchQuery.trim() && !searchLoading" class="p-3 text-center text-sm text-gray-500">
                          No products found. <button type="button" @click="handleSearch" class="font-semibold" style="color: #EDD97A;">Search all</button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>

                <!-- Navegación -->
                <div>
                  <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Menu</p>
                  <div class="flex flex-col gap-0.5">
                    <RouterLink to="/" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors">
                      Home
                    </RouterLink>
                    <RouterLink to="/products" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors">
                      Products
                    </RouterLink>
                    <RouterLink to="/about" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors">
                      About Us
                    </RouterLink>
                  </div>
                </div>

                <!-- Perfil / Cuenta -->
                <div>
                  <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Account</p>
                  <div class="flex flex-col gap-0.5">
                    <template v-if="userStore.isAuthenticated">
                      <RouterLink to="/profile" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors flex items-center gap-3">
                        <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        Profile
                      </RouterLink>
                      <RouterLink to="/orders" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors flex items-center gap-3">
                        <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                        My Orders
                      </RouterLink>
                      <RouterLink to="/terms" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg text-gray-800 font-medium hover:bg-white transition-colors flex items-center gap-3">
                        <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Terms and Conditions
                      </RouterLink>
                      <RouterLink v-if="userStore.isAdmin" to="/admin/dashboard" @click="closeMobileMenu" class="py-2.5 px-3 rounded-lg font-medium hover:bg-white transition-colors flex items-center gap-3" style="color: #EDD97A;">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /></svg>
                        Admin Panel
                      </RouterLink>
                      <button type="button" @click="handleMobileLogout" class="w-full py-2.5 px-3 rounded-lg text-left text-red-600 font-medium hover:bg-red-50 transition-colors flex items-center gap-3">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Logout
                      </button>
                    </template>
                    <template v-else>
                      <button type="button" @click="closeMobileMenu(); openLogin();" class="w-full py-2.5 px-3 rounded-lg text-left font-medium hover:bg-white transition-colors flex items-center gap-3 text-gray-800">
                        <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                        Log in
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useModalStore } from '@/stores/modal'
import { useProducts } from '@/composables/useProducts'
import { mapApiProductToEcommerce } from '@/utils/productMapper'
import LoginModal from '@/components/auth/LoginModal.vue'
import CartSidebar from '@/components/cart/CartSidebar.vue'
import UserMenu from '@/components/layout/UserMenu.vue'

const router = useRouter()
const cartStore = useCartStore()
const userStore = useUserStore()
const modalStore = useModalStore()
const isMobileMenuOpen = ref(false)
const searchQuery = ref('')
const showSearchResults = ref(false)
const scrollY = ref(0)
const headerBarRef = ref<HTMLElement | null>(null)

function updateHeaderHeight() {
  nextTick(() => {
    const el = headerBarRef.value
    if (el) {
      const h = el.offsetHeight
      document.documentElement.style.setProperty('--header-height', `${h}px`)
    }
  })
}

// Products for search
const { products: apiProducts, loading: searchLoading, fetchProducts } = useProducts()

// Filtered search results
const searchResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }
  
  const query = searchQuery.value.toLowerCase().trim()
  const mappedProducts = apiProducts.value.map(mapApiProductToEcommerce)
  
  return mappedProducts
    .filter(product => {
      const nameMatch = product.name.toLowerCase().includes(query)
      const descriptionMatch = product.description?.toLowerCase().includes(query)
      const categoryMatch = product.category?.toLowerCase().includes(query)
      
      return nameMatch || descriptionMatch || categoryMatch
    })
    .slice(0, 8) // Mostrar máximo 8 resultados
})

// Watch search query to show/hide results
watch(searchQuery, (newValue) => {
  showSearchResults.value = newValue.trim().length > 0 && searchResults.value.length > 0
})

// Computed properties for scroll behavior
const isScrolled = computed(() => scrollY.value > 50)
const showBanner = computed(() => scrollY.value < 50)

// Usar el store para el contador del carrito
const cartItemCount = computed(() => cartStore.itemCount)

const handleScroll = () => {
  scrollY.value = window.scrollY
}

// Handle click outside to close search results
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.search-container')) {
    showSearchResults.value = false
  }
}

onMounted(async () => {
  await fetchProducts()
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('resize', updateHeaderHeight)
  document.addEventListener('click', handleClickOutside)
  updateHeaderHeight()
})


onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', updateHeaderHeight)
  document.removeEventListener('click', handleClickOutside)
})

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const openLogin = () => {
  modalStore.openLoginModal()
}

const handleMobileLogout = () => {
  closeMobileMenu()
  userStore.logout()
  router.push('/')
}

const openCart = () => {
  cartStore.toggleCart()
}

// Handle search functionality
const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({
      path: '/products',
      query: { search: searchQuery.value.trim() }
    })
    // Close search results
    showSearchResults.value = false
    // Clear search query after navigation
    searchQuery.value = ''
    // Close mobile menu if open
    if (isMobileMenuOpen.value) {
      closeMobileMenu()
    }
  }
}

// Go to product detail
const goToProduct = (productId: string) => {
  showSearchResults.value = false
  searchQuery.value = ''
  router.push(`/product/${productId}`)
  if (isMobileMenuOpen.value) {
    closeMobileMenu()
  }
}
</script>

<style scoped>
/* Colapso del banner: sube y desaparece; el header sube junto (overflow-hidden en el padre) */
.banner-block {
  box-sizing: border-box;
}
.banner-collapse-enter-active,
.banner-collapse-leave-active {
  transition:
    max-height 0.32s cubic-bezier(0.32, 0.72, 0.2, 1),
    opacity 0.22s cubic-bezier(0.32, 0.72, 0.2, 1),
    padding 0.28s cubic-bezier(0.32, 0.72, 0.2, 1);
}
.banner-collapse-enter-from {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.banner-collapse-enter-to {
  max-height: 2.5rem;
  opacity: 1;
}
.banner-collapse-leave-from {
  max-height: 2.5rem;
  opacity: 1;
}
@media (max-width: 639px) {
  .banner-collapse-enter-to,
  .banner-collapse-leave-from {
    max-height: 1.75rem;
  }
}
.banner-collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
